import { useParams } from 'react-router-dom'

import { StatusPill } from '@/shared/components/common'
import { ErrorState, SkeletonRows } from '@/shared/components/data'
import { Markdown } from '@/shared/components/ui'
import { useI18n } from '@/shared/i18n'
import { countOrDash } from '@/shared/lib/format'

import { useProject } from '../api'

/**
 * El semáforo sale del catálogo COMÚN (`common.health.*`): el mismo estado se
 * lee igual acá, en el portafolio y en el portal. El tono es lo único propio de
 * esta pantalla.
 */
const HEALTH: Record<string, { key: string; tone: 'done' | 'overdue' | 'feedback' }> = {
  green: { key: 'common.health.green', tone: 'done' },
  yellow: { key: 'common.health.yellow', tone: 'feedback' },
  red: { key: 'common.health.red', tone: 'overdue' },
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-2xs tracking-wide text-text-3 uppercase">{label}</span>
      <span className="num text-xs text-text-2">{value}</span>
    </div>
  )
}

export function ProjectSummaryTab() {
  const { projectId = '' } = useParams()
  const { t, formatDate, formatNumber } = useI18n()
  const query = useProject(projectId)

  if (query.isPending) return <SkeletonRows rows={5} />
  if (query.isError || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }

  const project = query.data
  // `health` puede venir `null`: el proyecto puede no tenerlo puesto. Sin dato
  // no se pinta un semáforo verde inventado.
  const health = project.health ? (HEALTH[project.health] ?? null) : null

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_240px]">
      <section className="flex flex-col gap-3">
        <h2 className="text-base text-text-1">{t('projects.summary.brief')}</h2>
        {/* El `brief_md` lo escribió una persona: viaja tal cual, sin traducir. */}
        <Markdown empty={t('projects.summary.noBrief')}>{project.brief_md}</Markdown>
      </section>

      <aside className="hairline flex h-fit flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 p-3">
        <div className="flex flex-col gap-1">
          <span className="text-2xs tracking-wide text-text-3 uppercase">
            {t('projects.summary.health')}
          </span>
          {health ? (
            // `self-start`: el pill es `inline-flex`, y dentro de una columna
            // flex se estiraba a todo el ancho de la tarjeta — una caja de
            // color de 240px para decir dos palabras.
            <StatusPill tone={health.tone} variant="quiet" className="self-start">
              {t(health.key)}
            </StatusPill>
          ) : (
            <span className="text-xs text-text-3">{t('common.health.unknown')}</span>
          )}
        </div>

        <Meta label={t('projects.summary.start')} value={formatDate(project.start_date)} />
        <Meta
          label={t('projects.summary.targetEnd')}
          value={
            project.is_ongoing
              ? t('projects.summary.noEndDate')
              : formatDate(project.target_end_date)
          }
        />
        {project.actual_end_date ? (
          <Meta
            label={t('projects.summary.actualEnd')}
            value={formatDate(project.actual_end_date)}
          />
        ) : null}
        {/*
          Los contadores pueden ser `null` (el listado de la empresa todavía no
          llegó). `countOrDash` dibuja un guión: un `0` acá diría "no tiene
          ninguna tarea", que es distinto de "todavía no lo sé".
        */}
        <Meta
          label={t('projects.summary.openTasks')}
          value={project.open_tasks === null ? countOrDash(null) : formatNumber(project.open_tasks)}
        />
        <Meta
          label={t('projects.summary.overdueTasks')}
          value={
            project.overdue_tasks === null ? (
              countOrDash(null)
            ) : project.overdue_tasks > 0 ? (
              <span
                className="font-medium text-overdue"
                title={t('projects.summary.overdueCount', { count: project.overdue_tasks })}
              >
                <span aria-hidden="true">! </span>
                {formatNumber(project.overdue_tasks)}
              </span>
            ) : (
              formatNumber(0)
            )
          }
        />
        {/* `ProjectRead` no trae `updated_at`; lo que sí trae es cuándo se creó. */}
        <Meta label={t('common.label.created')} value={formatDate(project.created_at)} />
      </aside>
    </div>
  )
}
